import { useEffect, useRef, useState, type Ref } from "react";
import { toPng } from "html-to-image";
import { Mascot } from "../components/Mascot";
import { AFTER_INTRO, CARDS, DROP_IN, MORNING, SITE, TITLE, type GroupCard } from "../lib/card";
import "../far.css";
import "./Card.css";

/**
 * The cards for the group chat (/card, decisions 46 and 48): three pictures in the slides' look,
 * each on its own colour with Lord Cockington in his own mood: how it works, how the day runs and
 * what to bring. The page draws each card, makes it into a picture (a copy drawn out of sight at
 * the picture's own size, 4:5, which WhatsApp shows whole in the chat) and then shows that
 * picture: hold it to save it, Save under it, or Share all to send the three as one album. Under
 * them, apart, the drop-in card for anyone who said they cannot make it, with its own Save and
 * Share; Share all leaves it out. Between them, the rule cards (his ask, 18 Oct): the morning set
 * and the after the intro set, four each, each set with its own Share all. Needs no tournament and
 * no link, so it works before the day.
 */

/** A set of cards sent together as one album. */
interface CardSet {
  /** For the pictures' file names; empty for the first album, whose names stay as they were. */
  id: string;
  cards: readonly GroupCard[];
  /** Over the set, apart from the cards above it: what it is and when to send it. */
  note?: string;
  share: string;
}

const SETS: readonly CardSet[] = [
  { id: "", cards: CARDS, share: "Share all" },
  { id: "morning", cards: MORNING, note: "The rules, for the morning of the day. Send these four on Sunday morning.", share: "Share the morning four" },
  { id: "after", cards: AFTER_INTRO, note: "After the intro. Send these four once everyone has been through the slides.", share: "Share the after the intro four" },
];

/** Every card the page makes: the sets in order, then the drop-in card. */
const ALL: readonly GroupCard[] = [...SETS.flatMap((set) => set.cards), DROP_IN];
/** Where each set starts in ALL. */
const STARTS = SETS.map((_, k) => SETS.slice(0, k).reduce((n, set) => n + set.cards.length, 0));

type Picture = { url: string; file: File } | "making" | "failed";

/** One card, the same at any size: everything on it is sized from its own width. */
function Card({ card, className, ref }: { card: GroupCard; className?: string; ref?: Ref<HTMLElement> }) {
  if (card.poster) {
    return (
      <section ref={ref} className={className ? `far bdc ${className}` : "far bdc"} data-ground={card.ground}>
        <div className="bdc-poster">
          <p className="bdc-p-name wall-display">{card.poster.name}</p>
          <Mascot pose={card.pose} className="bdc-p-mascot" />
          <h1 className="bdc-p-call wall-display">{card.poster.call}</h1>
          <p className="bdc-p-says">{card.says}</p>
          {card.lines?.length ? (
            <ul className="bdc-p-strip">
              {card.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          <p className="bdc-p-when">
            <span className="wall-display">
              {TITLE.name} {TITLE.year}
            </span>
            <span>{TITLE.when}</span>
          </p>
          <p className="bdc-site">{SITE}</p>
        </div>
      </section>
    );
  }
  return (
    <section ref={ref} className={className ? `far bdc ${className}` : "far bdc"} data-ground={card.ground}>
      <div className={card.heading ? "bdc-in bdc-band" : "bdc-in"}>
        {card.heading ? (
          <div className="bdc-title">
            <div className="bdc-brand">
              <p className="bdc-name wall-display">{TITLE.name}</p>
              <p className="bdc-year wall-display">{TITLE.year}</p>
            </div>
            <p className="bdc-when">{TITLE.when}</p>
            <h1 className="bdc-heading wall-display">{card.heading}</h1>
          </div>
        ) : (
          <div className="bdc-title">
            <h1 className="bdc-name wall-display">{TITLE.name}</h1>
            <p className="bdc-sub">{TITLE.sub}</p>
            <p className="bdc-when">{TITLE.when}</p>
            <p className="bdc-year wall-display">{TITLE.year}</p>
          </div>
        )}
        {card.times ? (
          <ol className="bdc-lines">
            {card.times.map(([when, what]) => (
              <li key={when} className="bdc-time">
                <span className="bdc-time-when wall-display">{when}</span>
                <span className="bdc-time-what">{what}</span>
              </li>
            ))}
          </ol>
        ) : (
          <ul className={(card.lines?.length ?? 0) <= 5 ? "bdc-lines bdc-few" : "bdc-lines"}>
            {(card.lines ?? []).map((line) => (
              <li key={line} className="bdc-line">
                {line}
              </li>
            ))}
          </ul>
        )}
        <div className="bdc-host">
          <p className="bdc-says">{card.says}</p>
          <Mascot pose={card.pose} className="bdc-mascot" />
        </div>
        <p className="bdc-site">{SITE}</p>
      </div>
    </section>
  );
}

/** A copy out of sight as a PNG at its own size. Safari leaves the lettering out the first time, so that one is thrown away. */
async function makePicture(el: HTMLElement): Promise<Blob> {
  const opts = { pixelRatio: 1, cacheBust: false };
  await toPng(el, opts);
  const url = await toPng(el, opts);
  return (await fetch(url)).blob();
}

function fileName(i: number, card: GroupCard): string {
  const k = SETS.findIndex((set) => set.cards.includes(card));
  if (k < 0) return `bdayminton-2026-${card.id}.png`;
  const set = SETS[k]!;
  return `bdayminton-2026-${set.id ? `${set.id}-` : ""}${i - STARTS[k]! + 1}-${card.id}.png`;
}

export function CardView() {
  const shots = useRef<(HTMLElement | null)[]>([]);
  const [pictures, setPictures] = useState<Picture[]>(() => ALL.map(() => "making"));

  useEffect(() => {
    document.title = "BDayminton 2026";
    let live = true;
    const urls: string[] = [];
    // one at a time, so a phone is not making three at once
    void (async () => {
      await document.fonts.ready;
      for (const [i, card] of ALL.entries()) {
        const el = shots.current[i];
        let made: Picture = "failed";
        if (el) {
          try {
            const blob = await makePicture(el);
            const url = URL.createObjectURL(blob);
            urls.push(url);
            made = { url, file: new File([blob], fileName(i, card), { type: "image/png" }) };
          } catch {
            made = "failed";
          }
        }
        if (!live) return;
        setPictures((now) => now.map((p, j) => (j === i ? made : p)));
      }
    })();
    return () => {
      live = false;
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, []);

  /** A set's pictures, once every one of them is made and this phone can share them together. */
  const shareable = (k: number): File[] | null => {
    const set = SETS[k]!;
    const files = pictures.slice(STARTS[k]!, STARTS[k]! + set.cards.length).flatMap((p) => (typeof p === "object" ? [p.file] : []));
    return files.length === set.cards.length && typeof navigator.canShare === "function" && navigator.canShare({ files }) ? files : null;
  };
  const files = shareable(0);
  const canShareAll = files !== null;
  const making = pictures.includes("making");

  return (
    <main className="card-page">
      <div className="close card-tools">
        <p className="card-hint">
          {making
            ? "Making the pictures"
            : canShareAll
              ? "Hold a picture to save it, tap Save under it, or Share all to send the three to the group. The rule cards further down have a Share of their own."
              : "Hold a picture to save it, or tap Save under it."}
        </p>
        {canShareAll ? (
          <button type="button" className="btn" onClick={() => void navigator.share({ files: files! }).catch(() => {})}>
            Share all
          </button>
        ) : null}
      </div>
      {ALL.map((card, i) => {
        const p = pictures[i];
        const made = typeof p === "object" ? p : null;
        const alone = made && typeof navigator.canShare === "function" && navigator.canShare({ files: [made.file] });
        const k = STARTS.indexOf(i);
        const set = k > 0 ? SETS[k]! : null;
        const setFiles = set ? shareable(k) : null;
        return (
          <section key={card.id} className="card-one">
            {set?.note ? <p className="close card-apart">{set.note}</p> : null}
            {setFiles ? (
              <div className="close card-tools">
                <button type="button" className="btn" onClick={() => void navigator.share({ files: setFiles }).catch(() => {})}>
                  {set!.share}
                </button>
              </div>
            ) : null}
            {card === DROP_IN ? <p className="close card-apart">For anyone who said they can't make it. Send this one on its own.</p> : null}
            {made ? (
              <img className="card-picture" src={made.url} alt={`${TITLE.name} ${TITLE.year}: ${card.poster ? `${card.poster.name} ${card.poster.call}` : (card.heading ?? "how it works")}, from Lord Cockington`} />
            ) : (
              <Card card={card} />
            )}
            <div className="close card-tools">
              {made ? (
                <a className="btn quiet" href={made.url} download={made.file.name}>
                  Save
                </a>
              ) : null}
              {made && card === DROP_IN && alone ? (
                <button type="button" className="btn" onClick={() => void navigator.share({ files: [made.file] }).catch(() => {})}>
                  Share
                </button>
              ) : p === "failed" ? (
                <p className="card-hint">This phone would not make this picture. Take a screenshot of the card instead.</p>
              ) : null}
            </div>
          </section>
        );
      })}
      {/* the copies that are made into the pictures, at the picture's own size, off the left edge */}
      <div className="bdc-offstage" aria-hidden="true">
        {ALL.map((card, i) => (
          <Card
            key={card.id}
            card={card}
            className="bdc-full"
            ref={(el) => {
              shots.current[i] = el;
            }}
          />
        ))}
      </div>
    </main>
  );
}
